// ---------------------------------------------------------------------------
// TestSet Service — Real implementation backed by the Teams Eval Data Hub API.
// ---------------------------------------------------------------------------

import type { IHttpClient } from '../api/client.js';

import type { DataEntry } from './dataHubService.interface.js';
import type {
  CreateTestSetRequest,
  ITestSetService,
  SearchTestSetsParams,
  TestSet,
  TestSetDataListParams,
  TestSetDataListResult,
  TestSetSearchResult,
  TestSetVersionListParams,
  TestSetVersionListResult,
  UpdateTestSetRequest,
} from './testSetService.interface.js';
import type { UsageContextOverrides } from './usageContext.interface.js';
import {
  optionalUsageContextRequestOptions,
  TEST_SET_USAGE_ACTIONS,
} from './usageContext.js';

// ── Raw API types ───────────────────────────────────────────────────────────

interface ApiTestSet {
  id: string;
  testSetId: string;
  dataSetId: string;
  tenantId?: string;
  ownerUserObjectId?: string;
  creatorName?: string;
  name: string;
  description?: string;
  status: string;
  sensitivityLabel?: string;
  accessCategory?: string;
  version: number;
  createdDateTime?: string;
  lastUpdatedDateTime?: string;
  properties?: Record<string, unknown>;
}

interface ApiTestSetSearchResponse {
  testSets: ApiTestSet[];
  continuationToken?: string | null;
  totalCount?: number | null;
  pageSize: number;
}

interface ApiTestSetVersionHistoryResponse {
  versions?: ApiTestSet[] | null;
  continuationToken?: string | null;
  totalCount?: number | null;
}

interface ApiGenericData {
  id: string;
  dataId?: string;
  name?: string;
  dataType?: string;
  createdDateTime?: string;
  lastUpdatedDateTime?: string;
  version: number;
  properties?: Record<string, unknown>;
}

interface ApiListDataResponse {
  data?: ApiGenericData[] | null;
  totalCount?: number | null;
  continuationToken?: string | null;
  pageSize: number;
}

// ── API path constants ──────────────────────────────────────────────────────

const SEARCH_TESTSETS_PATH = 'testsets/search';
const CREATE_TESTSET_PATH = 'testsets';
const testSetPath = (id: string) => `testsets/${id}`;
const testSetPublishPath = (id: string) => `testsets/${id}/publish`;
const testSetDraftPath = (id: string) => `testsets/${id}/draft`;
const testSetVersionsPath = (id: string) => `testsets/${id}/versions`;
const testSetDraftDeletePath = (id: string) => `testsets/${id}/versions/draft`;
const testSetDataPath = (id: string) => `testsets/${id}/data`;

// ── Mapping helpers ─────────────────────────────────────────────────────────

const API_DATA_TYPE_MAP: Record<string, string> = {
  generic: 'Generic',
  meetingData: 'Meeting Data',
  chatData: 'Chat Data',
  fileData: 'File Data',
  annotationData: 'Annotation Data',
};

function mapStatus(s: string): TestSet['status'] {
  switch (s.toLowerCase()) {
    case 'draft':
      return 'Draft';
    case 'published':
      return 'Published';
    case 'versioned':
      return 'Versioned';
    default:
      return 'Draft';
  }
}

function mapAccessLevel(category: string | undefined): TestSet['accessLevel'] {
  if (category === 'public') return 'Public';
  if (category === 'restricted') return 'Restricted';
  return undefined;
}

function mapTestSet(api: ApiTestSet): TestSet {
  return {
    id: api.testSetId ?? api.id,
    testSetId: api.testSetId ?? api.id,
    dataSetId: api.dataSetId,
    name: api.name,
    description: api.description,
    version: api.version,
    status: mapStatus(api.status),
    createdAt: api.createdDateTime,
    updatedAt: api.lastUpdatedDateTime,
    accessLevel: mapAccessLevel(api.accessCategory),
    dataType: api.status
      ? (API_DATA_TYPE_MAP[api.properties?.dataType as string] ?? undefined)
      : undefined,
    creatorName: api.creatorName ?? undefined,
    ownerUserObjectId: api.ownerUserObjectId,
    tenantId: api.tenantId,
  };
}

function mapDataEntry(api: ApiGenericData): DataEntry {
  return {
    id: api.id,
    dataId: api.dataId ?? api.id,
    name: api.name,
    dataType: api.dataType,
    createdAt: api.createdDateTime,
    updatedAt: api.lastUpdatedDateTime,
    version: api.version,
    properties: api.properties,
  };
}

// ── Service class ───────────────────────────────────────────────────────────

export class TestSetService implements ITestSetService {
  constructor(private readonly client: IHttpClient) {}

  async searchTestSets(
    params: SearchTestSetsParams,
  ): Promise<TestSetSearchResult> {
    const trimmedQuery = params.query?.trim();
    const { data } = await this.client.post<ApiTestSetSearchResponse>(
      SEARCH_TESTSETS_PATH,
      {
        query: trimmedQuery !== '' ? trimmedQuery : undefined,
        ownerUserObjectId: params.ownerUserObjectId,
        dataSetIds: params.dataSetIds,
        pageSize: params.pageSize ?? 20,
        continuationToken: params.continuationToken,
        includeCount: params.includeCount ?? true,
      },
    );

    return {
      items: (data.testSets ?? []).map(mapTestSet),
      totalCount: data.totalCount ?? null,
      continuationToken: data.continuationToken ?? null,
    };
  }

  async createTestSet(
    request: CreateTestSetRequest,
    usage?: UsageContextOverrides,
  ): Promise<TestSet> {
    const { data } = await this.client.post<ApiTestSet>(
      CREATE_TESTSET_PATH,
      {
        dataSetId: request.dataSetId,
        name: request.name,
        sensitivityLabel: request.sensitivityLabel,
        accessCategory: request.accessCategory,
        description: request.description,
      },
      optionalUsageContextRequestOptions(TEST_SET_USAGE_ACTIONS.CREATE, usage),
    );
    return mapTestSet(data);
  }

  async getTestSet(testSetId: string, version?: number): Promise<TestSet> {
    const query =
      version !== undefined
        ? new URLSearchParams({ version: String(version) })
        : undefined;
    const url = query
      ? `${testSetPath(testSetId)}?${query.toString()}`
      : testSetPath(testSetId);
    const { data } = await this.client.get<ApiTestSet>(url);
    return mapTestSet(data);
  }

  async updateTestSet(
    testSetId: string,
    request: UpdateTestSetRequest,
  ): Promise<TestSet> {
    const { data } = await this.client.put<ApiTestSet>(testSetPath(testSetId), {
      name: request.name,
      description: request.description,
    });
    return mapTestSet(data);
  }

  async deleteTestSet(
    testSetId: string,
    usage?: UsageContextOverrides,
  ): Promise<void> {
    await this.client.delete(
      testSetPath(testSetId),
      undefined,
      optionalUsageContextRequestOptions(TEST_SET_USAGE_ACTIONS.DELETE, usage),
    );
  }

  async publishTestSet(
    testSetId: string,
    usage?: UsageContextOverrides,
  ): Promise<TestSet> {
    const { data } = await this.client.patch<ApiTestSet>(
      testSetPublishPath(testSetId),
      undefined,
      optionalUsageContextRequestOptions(TEST_SET_USAGE_ACTIONS.PUBLISH, usage),
    );
    return mapTestSet(data);
  }

  async createDraftTestSet(
    testSetId: string,
    usage?: UsageContextOverrides,
  ): Promise<TestSet> {
    const { data } = await this.client.post<ApiTestSet>(
      testSetDraftPath(testSetId),
      undefined,
      optionalUsageContextRequestOptions(
        TEST_SET_USAGE_ACTIONS.CREATE_DRAFT,
        usage,
      ),
    );
    return mapTestSet(data);
  }

  async deleteTestSetDraft(
    testSetId: string,
    usage?: UsageContextOverrides,
  ): Promise<void> {
    await this.client.delete(
      testSetDraftDeletePath(testSetId),
      undefined,
      optionalUsageContextRequestOptions(TEST_SET_USAGE_ACTIONS.DELETE, usage),
    );
  }

  async listTestSetVersions(
    params: TestSetVersionListParams,
  ): Promise<TestSetVersionListResult> {
    const query = new URLSearchParams({
      pageSize: String(params.pageSize ?? 20),
      includeCount: String(params.includeCount ?? true),
    });
    if (params.continuationToken) {
      query.set('continuationToken', params.continuationToken);
    }
    const { data } = await this.client.get<ApiTestSetVersionHistoryResponse>(
      `${testSetVersionsPath(params.testSetId)}?${query.toString()}`,
    );
    return {
      items: (data.versions ?? []).map(mapTestSet),
      totalCount: data.totalCount ?? null,
      continuationToken: data.continuationToken ?? null,
    };
  }

  async addTestSetData(
    testSetId: string,
    dataIds: string[],
    usage?: UsageContextOverrides,
  ): Promise<void> {
    await this.client.post(
      testSetDataPath(testSetId),
      dataIds,
      optionalUsageContextRequestOptions(
        TEST_SET_USAGE_ACTIONS.ADD_DATA,
        usage,
      ),
    );
  }

  async listTestSetData(
    params: TestSetDataListParams,
  ): Promise<TestSetDataListResult> {
    const query = new URLSearchParams({
      version: String(params.version),
      pageSize: String(params.pageSize ?? 20),
      includeCount: String(params.includeCount ?? true),
    });
    if (params.continuationToken) {
      query.set('continuationToken', params.continuationToken);
    }
    const { data } = await this.client.get<ApiListDataResponse>(
      `${testSetDataPath(params.testSetId)}?${query.toString()}`,
    );
    return {
      items: (data.data ?? []).map(mapDataEntry),
      totalCount: data.totalCount ?? null,
      continuationToken: data.continuationToken ?? null,
    };
  }

  async deleteTestSetData(
    testSetId: string,
    dataIds: string[],
    usage?: UsageContextOverrides,
  ): Promise<void> {
    await this.client.delete(
      testSetDataPath(testSetId),
      dataIds,
      optionalUsageContextRequestOptions(
        TEST_SET_USAGE_ACTIONS.DELETE_DATA,
        usage,
      ),
    );
  }
}
